import { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Form, Dropdown, Badge } from 'react-bootstrap';
import { FiSearch, FiUser, FiShoppingCart, FiChevronDown, FiChevronRight, FiLogOut, FiPhone, FiHeart, FiHeadphones, FiShield, FiTruck, FiMenu, FiX, FiMonitor, FiBarChart2 } from 'react-icons/fi';
import { AiFillHome } from 'react-icons/ai';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { useSiteData } from '../../context/SiteDataContext';
import { productService } from '../../services/productService';
import { useCart } from '../../context/CartContext';
import { debounce } from '../../utils/debounce';
import config from '../../config';
import './Header.css';


// MegaMenuCascade removed to adopt StarTech-style nested dropdowns

const Header = memo(() => {


  const { user, isAuthenticated, logout } = useAuth();
  const { settings, categoryTree } = useSiteData();
  const { cartCount } = useCart();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [, setIsSearching] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [headerHeight, setHeaderHeight] = useState(0);
  const mobileSearchRef = useRef(null);
  const searchRef = useRef(null);
  const navRef = useRef(null);
  const headerRef = useRef(null);



  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const shouldBeSticky = scrollY > 120;
      
      if (shouldBeSticky !== isSticky) {
        if (shouldBeSticky && headerRef.current) {
          setHeaderHeight(headerRef.current.offsetHeight);
        }
        setIsSticky(shouldBeSticky);
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isSticky]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearch(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 991 && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Debounced search function
  const performSearch = useCallback(async (query) => {
    if (query.length >= 2) {
      setIsSearching(true);
      try {
        const data = await productService.getProducts({ search: query, limit: 5 });
        setSearchResults(data?.items || data || []);
        setShowSearch(true);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    } else {
      setSearchResults([]);
      setShowSearch(false);
    }
  }, []);

  // Memoized debounced function - 300ms delay
  const debouncedSearch = useMemo(
    () => debounce(performSearch, 300),
    [performSearch]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedSearch(query);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
      setShowSearch(false);
      setSearchQuery('');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const toggleCategory = (categoryId, e) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const handleCategoryClick = () => {
    setMobileMenuOpen(false);
    setExpandedCategories(new Set());
  };

  const navCategories = categoryTree; // Show all on mobile, limit on desktop

  return (
    <>
    <header ref={headerRef} className={`header ${isSticky ? 'sticky' : ''}`}>
      {/* Top Bar - Simple Features */}
      <div className="top-bar">
        <Container>
          <div className="top-bar-inner">
            <div className="top-feature">
              <FiHeadphones />
              <span>24/7 Support</span>
            </div>
            <div className="top-feature">
              <FiShield />
              <span>Secure Payment</span>
            </div>
            <div className="top-feature">
              <FiTruck />
              <span>Fast Delivery</span>
            </div>
            <div className="top-feature">
              <FiPhone />
              <span><a href={`tel:${config.contact.primaryPhone}`} style={{color: 'inherit', textDecoration: 'none'}}>{config.contact.primaryPhone}</a></span>
            </div>
          </div>
        </Container>
      </div>

      {/* Main Header */}
      <div className="main-header">
        <Container>
          <div className="header-inner">
            {/* Mobile Hamburger - visible only on mobile */}
            <button
              className="mobile-header-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <FiX size={22} /> : <FiMenu size={22} />}
            </button>

            {/* Logo */}
            <Link to="/" className="logo">
              <img 
                src={settings.logo_url || '/images/logo.png'} 
                alt={settings.site_name || 'FK It Solution'} 
                className="logo-img" 
                width="135"
                height="45"
              />
            </Link>

            {/* Search Bar */}
            <div className="search-wrapper" ref={searchRef}>
              <Form onSubmit={handleSearchSubmit} className="search-form">
                <Form.Control
                  type="search"
                  placeholder="Search for products..."
                  value={searchQuery}
                  onChange={handleSearch}
                  className="search-input"
                />
                <button type="submit" className="search-btn">
                  <FiSearch size={20} />
                </button>
              </Form>
              
              {showSearch && searchResults.length > 0 && (
                <div className="search-dropdown">
                  {searchResults.map((product) => (
                    <Link
                      key={product.id}
                      to={`/products/${product.slug}`}
                      className="search-item"
                      onClick={() => { setShowSearch(false); setSearchQuery(''); }}
                    >
                      <img
                        src={product.images?.[0]?.image_url || '/placeholder.png'}
                        alt={product.name}
                        className="search-item-img"
                        width="50"
                        height="50"
                      />
                      <div className="search-item-info">
                        <span className="search-item-name">{product.name}</span>
                        <span className="search-item-price">৳{product.price?.toLocaleString()}</span>
                      </div>
                    </Link>
                  ))}
                  <Link 
                    to={`/products?search=${encodeURIComponent(searchQuery)}`} 
                    className="search-view-all"
                    onClick={() => { setShowSearch(false); setSearchQuery(''); }}
                  >
                    View all results
                  </Link>
                </div>
              )}
            </div>

            {/* Header Actions */}
            <div className="header-actions">
              {/* Mobile Search Icon - only visible on mobile */}
              <button
                className="mobile-search-toggle action-btn"
                onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
                title="Search"
              >
                <FiSearch size={22} />
              </button>

              {/* Wishlist */}
              <Link to="/wishlist" className="action-btn" title="Wishlist">
                <FiHeart size={22} />
                <span className="action-label">Wishlist</span>
              </Link>

              {/* Account */}
              {isAuthenticated ? (
                <div className="d-flex align-items-center bg-transparent rounded action-group">
                  <Link to="/profile" className="action-btn pe-2" title="My Profile">
                    <FiUser size={22} />
                    <span className="action-label">{user?.full_name?.split(' ')[0]}</span>
                  </Link>
                  <Dropdown align="end" className="account-dropdown">
                    <Dropdown.Toggle as="div" className="action-btn ps-1 pe-2" style={{minWidth: 'auto', borderLeft: '1px solid rgba(255,255,255,0.1)', borderRadius: '0 6px 6px 0'}}>
                      <FiChevronDown size={16} className="mt-3" />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item as={Link} to="/profile">My Profile</Dropdown.Item>
                      <Dropdown.Item as={Link} to="/orders">My Orders</Dropdown.Item>
                      {['viewer', 'editor', 'manager', 'admin', 'super_admin'].includes(user?.role) && (
                        <Dropdown.Item as={Link} to="/admin">Admin Panel</Dropdown.Item>
                      )}
                      <Dropdown.Divider />
                      <Dropdown.Item onClick={handleLogout}>
                        <FiLogOut className="me-2" /> Logout
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              ) : (
                <Link to="/login" className="action-btn" title="Login">
                  <FiUser size={22} />
                  <span className="action-label">Login</span>
                </Link>
              )}

              {/* Cart */}
              <Link to="/cart" className="action-btn cart-action" title="Cart">
                <FiShoppingCart size={22} />
                {cartCount > 0 && (
                  <Badge bg="danger" className="cart-badge">{cartCount > 99 ? '99+' : cartCount}</Badge>
                )}
                <span className="action-label">Cart</span>
              </Link>

              {/* PC Config */}
              <Link
                to="/pc-config"
                className="pc-config-btn"
                title="PC Builder"
              >
                <FiMonitor size={20} />
                <span>PC Config</span>
              </Link>

              {/* Compare */}
              <Link
                to="/compare"
                className="pc-config-btn"
                title="Compare Products"
              >
                <FiBarChart2 size={20} />
                <span>Compare</span>
              </Link>
            </div>
          </div>
        </Container>
      </div>

      {/* Mobile Search Expandable */}
      {mobileSearchOpen && (
        <div className="mobile-search-bar" ref={mobileSearchRef}>
          <Container>
            <Form onSubmit={(e) => { handleSearchSubmit(e); setMobileSearchOpen(false); }} className="mobile-search-form">
              <Form.Control
                type="search"
                placeholder="Search for products..."
                value={searchQuery}
                onChange={handleSearch}
                className="mobile-search-input"
                autoFocus
              />
              <button type="submit" className="mobile-search-submit">
                <FiSearch size={18} />
              </button>
              <button type="button" className="mobile-search-close" onClick={() => { setMobileSearchOpen(false); setSearchQuery(''); setShowSearch(false); }}>
                <FiX size={18} />
              </button>
            </Form>
            {showSearch && searchResults.length > 0 && (
              <div className="mobile-search-dropdown">
                {searchResults.map((product) => (
                  <Link
                    key={product.id}
                    to={`/products/${product.slug}`}
                    className="search-item"
                    onClick={() => { setShowSearch(false); setSearchQuery(''); setMobileSearchOpen(false); }}
                  >
                    <img
                      src={product.images?.[0]?.image_url || '/placeholder.png'}
                      alt={product.name}
                      className="search-item-img"
                      width="40"
                      height="40"
                    />
                    <div className="search-item-info">
                      <span className="search-item-name">{product.name}</span>
                      <span className="search-item-price">৳{product.price?.toLocaleString()}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </Container>
        </div>
      )}

      {/* Category Navigation Bar - Like Startech */}
      <nav className="category-nav" ref={navRef} style={{overflow: 'visible'}}>
        <Container style={{overflow: 'visible'}}>
          {/* Mobile Menu Toggle */}
          <button 
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <FiMenu size={24} />
            <span>Categories</span>
          </button>

          {/* Category List */}
          <ul className={`category-nav-list ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            {/* Mobile Menu Header */}
            <div className="mobile-menu-header">
              <span>All Categories</span>
              <button 
                className="mobile-menu-close"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
              >
                <FiX size={24} />
              </button>
            </div>

            {/* Home Icon */}
            <li className="category-nav-item home-nav-item">
              <Link to="/" className="category-nav-link home-nav-link">
                <AiFillHome size={18} />
              </Link>
            </li>

            {navCategories.map((category, index) => {
              return (
              <li 
                key={category.id} 
                className={`category-nav-item has-standard-menu 
                  ${category.children?.length > 0 ? 'has-submenu' : ''} 
                  ${expandedCategories.has(category.id) ? 'expanded' : ''}
                  ${index > navCategories.length - 5 ? 'nav-item-right' : ''}
                `}
              >
                <div className="category-nav-link-wrapper">
                  <Link 
                    to={`/categories/${category.slug}`} 
                    className="category-nav-link"
                    onClick={handleCategoryClick}
                  >
                    {category.name}
                    {category.children?.length > 0 && <FiChevronDown className="ms-1 d-none d-lg-inline" />}
                  </Link>
                  {category.children?.length > 0 && (
                    <button 
                      className="category-toggle-btn d-lg-none"
                      onClick={(e) => toggleCategory(category.id, e)}
                      aria-label="Toggle submenu"
                    >
                      <FiChevronDown size={16} />
                    </button>
                  )}
                </div>
                
                {category.children && category.children.length > 0 && (
                  <div className={`submenu-dropdown standard-dropdown ${expandedCategories.has(category.id) ? 'mobile-expanded' : ''}`}>
                    <div className="submenu-inner">
                        <ul className="submenu-list">
                          {category.children.map((sub) => (
                            <li key={sub.id} className="submenu-item">
                              <Link 
                                to={`/categories/${sub.slug}`} 
                                className="submenu-link"
                                onClick={handleCategoryClick}
                              >
                                {sub.name}
                                {sub.children?.length > 0 && <FiChevronRight size={14} className="submenu-chevron" />}
                              </Link>
                              {sub.children?.length > 0 && (
                                <ul className="submenu-child-list">
                                  {sub.children.map((child) => (
                                    <li key={child.id}>
                                      <Link 
                                        to={`/categories/${child.slug}`} 
                                        className="submenu-child-link"
                                        onClick={handleCategoryClick}
                                      >
                                        {child.name}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </li>
                          ))}
                          <li className="submenu-item submenu-view-all">
                            <Link 
                              to={`/categories/${category.slug}`} 
                              className="submenu-link view-all-link"
                              onClick={handleCategoryClick}
                            >
                              View All
                            </Link>
                          </li>
                        </ul>
                    </div>
                  </div>
                )}
              </li>
              );
            })}

            <li className="category-nav-item">
              <Link 
                to="/products?featured=true" 
                className="category-nav-link offers-link"
                onClick={() => setMobileMenuOpen(false)}
              >
                Offers
              </Link>
            </li>
          </ul>
        </Container>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="mobile-menu-overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </header>

    {/* Mobile Bottom Nav Bar */}
    <nav className="mobile-bottom-nav">
      <Link to="/wishlist" className="mobile-bottom-item">
        <FiHeart size={20} />
        <span>Wishlist</span>
      </Link>
      <Link to="/products?featured=true" className="mobile-bottom-item">
        <FiTruck size={20} />
        <span>Offers</span>
      </Link>
      <button
        className="mobile-bottom-item pc-builder-bottom"
        onClick={() => toast.info('🔧 PC Config feature coming soon! Stay tuned.', { autoClose: 3000, hideProgressBar: false })}
      >
        <FiMonitor size={20} />
        <span>PC Builder</span>
      </button>
      {isAuthenticated ? (
        <Link to="/profile" className="mobile-bottom-item">
          <FiUser size={20} />
          <span>Account</span>
        </Link>
      ) : (
        <Link to="/login" className="mobile-bottom-item">
          <FiUser size={20} />
          <span>Account</span>
        </Link>
      )}
    </nav>

    {isSticky && <div style={{ height: `${headerHeight}px` }} />}
    </>
  );
});

export default Header;
